import React, {useEffect, useState} from "react";
import useCalculator from "../../hooks/useCalculator";
import usePrice from "../../hooks/usePrice";
import {useFormState} from "react-final-form";
import {useSelector} from "react-redux";
import {useIsShowMap} from "../../hooks/useIsShowMap";

const UpsalePopup = ({ isOpen, onClose, onUpgrade, onContinue }) => {
    const {writerLevel, features} = useSelector(state => state.orderOptions.data);
    const {price, calculateWriterPricePerPage, calculateFeaturePrice, customerBalance} = useCalculator();
    const {priceFormat} = usePrice();
    const {values} = useFormState();
    const [priceAtOpen, setPriceAtOpen] = useState(null);

    const isShowMap = useIsShowMap(values);
    let canShowPlagiarism = true;
    const isSetPages = parseInt(values.page) || 0;

    if (isSetPages === 0) {
        canShowPlagiarism = false;
    } else if (isShowMap.freePlagiarismReport && values.freePlagiarismReport === 1) {
        canShowPlagiarism = false;
    } else if (isShowMap.plagiarismReport && values.plagiarismReport === 1) {
        canShowPlagiarism = false;
    }

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("no-scroll");
            setPriceAtOpen(price);
        } else {
            document.body.classList.remove("no-scroll");
            setPriceAtOpen(null);
        }

        return () => {
            document.body.classList.remove("no-scroll");
        };
    }, [isOpen]);

    const basePrice = priceAtOpen !== null ? priceAtOpen : price;

    if (!isOpen) return null;

    const rawUpgradePrice = () => {
        let upgrade;
        if(values.authorCategory === "Advanced"){
            if(parseInt(values.page) === 0){
                upgrade = basePrice - calculateWriterPricePerPage(writerLevel.advanced) + (calculateWriterPricePerPage(writerLevel.premiumWriter));
            }else{
                upgrade = basePrice - calculateWriterPricePerPage(writerLevel.advanced) + (parseInt(values.page) * calculateWriterPricePerPage(writerLevel.premiumWriter)) + calculateFeaturePrice(features.plagiarismReport);
            }
        }else{
            if(parseInt(values.page) === 0){
                upgrade = basePrice + (calculateWriterPricePerPage(writerLevel.premiumWriter));
            }else{
                upgrade = basePrice + (parseInt(values.page) * calculateWriterPricePerPage(writerLevel.premiumWriter)) + calculateFeaturePrice(features.plagiarismReport);
            }
        }

        return upgrade;
    };

    // Both buttons surface the same final number the sidebar shows — i.e.
    // post-coupon AND post-balance. Without this, the popup would say
    // "Continue without upgrade for $42.59" while the sidebar shows $0.00,
    // and the customer would rightfully think the math is broken.
    const useBalance = !!values.useBalance;
    const continueFinal = useBalance
        ? Math.max(0, basePrice - customerBalance)
        : basePrice;
    const upgradeFinal = useBalance
        ? Math.max(0, rawUpgradePrice() - customerBalance)
        : rawUpgradePrice();

    return (
        <div className="upsale-popup-overlay">
            <div className="upsale-popup-content">
                <div className="upsale-popup-content-title">Upgrade Your Paper</div>
                <button type="button" className="upsale-popup-content__close" onClick={onClose}></button>
                <div className="upsale-popup-content-box">
                    <div className="upsale-popup-content-plain">
                        <div className="upsale-popup-content-plain-title">
                            <img src="/_core/assets/themes/v5/assets/img/form3/mingcute_check-2-fill.svg" alt="" />
                            Plain Order
                        </div>
                        <div className="upsale-popup-content-plain-block">
                            <div className="upsale-popup-content-plain-block__item">
                                <div className="upsale-popup-content-plain-block__item-title">
                                    <img src="/_core/assets/themes/v5/assets/img/form3/plain1.svg" alt="" />
                                    Regular Writer
                                </div>
                                <p className="upsale-popup-content-plain-block__item-text">
                                    Your paper will still be in good hands, but you may miss out on the refined language and nuances only a native English speaker can provide.
                                </p>
                            </div>

                            {canShowPlagiarism && (
                                <div>
                                    <span className="upsale-popup-content-plain-block__line"></span>
                                    <div className="upsale-popup-content-plain-block__item">
                                        <div className="upsale-popup-content-plain-block__item-title">
                                            <img src="/_core/assets/themes/v5/assets/img/form3/plain2.svg" alt="" />
                                            Plagiarism Report Not Included
                                        </div>
                                        <p className="upsale-popup-content-plain-block__item-text">
                                            Your paper will be written from scratch, but without this feature, you won’t have an official originality report
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="upsale-popup-content-upgraded">
                        <span className="upsale-popup-content-upgraded-line"></span>
                        <div className="upsale-popup-content-upgraded-title">
                            <img src="/_core/assets/themes/v5/assets/img/form3/lightning-01.svg" alt="" />
                            Upgraded Order
                        </div>
                        <div className="upsale-popup-content-upgraded-block">
                            <div className="upsale-popup-content-upgraded-block__item">
                                <div className="upsale-popup-content-upgraded-block__item-title">
                                    <img src="/_core/assets/themes/v5/assets/img/form3/upgraded1.svg" alt="" />
                                    Premium Writer
                                </div>
                                <p className="upsale-popup-content-upgraded-block__item-text">
                                    Native English-speaking writer with advanced vocabulary and style, ensuring the highest academic standards.
                                </p>
                            </div>

                            {canShowPlagiarism && (
                                <div>
                                    <span className="upsale-popup-content-upgraded-block__line"></span>
                                    <div className="upsale-popup-content-upgraded-block__item">
                                        <div className="upsale-popup-content-upgraded-block__item-title">
                                            <img src="/_core/assets/themes/v5/assets/img/form3/upgraded2.svg" alt="" />
                                            Plagiarism Report
                                        </div>
                                        <p className="upsale-popup-content-upgraded-block__item-text">
                                            Want peace of mind? Add an official plagiarism report to your order and receive proof that your paper is 100% original.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="upsale-popup-content-links">
                    <button
                        type="button"
                        className="upsale-popup-content-links__upgrade"
                        onClick={onUpgrade}>
                        <img src="/_core/assets/themes/v5/assets/img/form3/lightning-01.svg" alt="" />
                        <span>
                            Upgrade - <span className="upsale-popup-content-links__upgrade-price">{priceFormat(upgradeFinal)}</span>
                        </span>
                    </button>
                    <button
                        type="button"
                        className="upsale-popup-content-links__cou"
                        onClick={onContinue}>
                        Continue Without Upgrade For {priceFormat(continueFinal)}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpsalePopup;
